/**
 * Reload_Guard decision logic (auto-reload-on-deploy)
 *
 * A single pure, side-effect-free function that decides whether a router
 * navigation should be converted into a full-page reload (via
 * `window.location.assign(to.fullPath)`) instead of a normal SPA transition.
 *
 * It is deliberately separated from the router `beforeEach` guard (task 8.2)
 * and from the Version_Checker singleton (versionCheck.js) so it can be
 * property-tested without a real router, real DOM, or real `window.location`.
 * The router-wiring task (8.2) is responsible for gathering the four inputs
 * from the live navigation context, calling this function, and performing the
 * actual side effects (console log + `window.location.assign` + `next(false)`).
 *
 * Requirements: 8.1, 8.2, 8.6.
 */

/**
 * The outcome the existing `beforeEach` auth logic produced for a navigation.
 *
 * The router guard has exactly two relevant kinds of outcome for `to`:
 *   - a plain allow  (`next()` with no argument) -> the navigation proceeds
 *     to `to` as an SPA transition. Represented as
 *     `{ allowed: true, isRedirect: false }`.
 *   - an auth-issued redirect (`next('/')`, `next('/login')`,
 *     `next('/forgot-password')`, ...) -> the navigation is diverted to a
 *     different target by auth. Represented as
 *     `{ allowed: false, isRedirect: true }` (or `allowed: true` with
 *     `isRedirect: true` — either is treated as "not a plain allow").
 *
 * A full-page reload is only ever appropriate for a plain allow: the user is
 * being allowed to proceed to the destination they asked for. Blocks and
 * auth-issued redirects must proceed as normal SPA transitions so that auth
 * allow/block/redirect outcomes are preserved byte-for-byte and no reload loop
 * can form on a redirect target (Requirement 8.6).
 *
 * @typedef {Object} AuthDecision
 * @property {boolean} allowed    true when auth called plain `next()` to let
 *                                the navigation proceed to `to`.
 * @property {boolean} [isRedirect] true when auth issued a redirect
 *                                (`next('/somewhere')`). Defaults to false.
 */

/**
 * A minimal view of a Vue Router route location. Only `fullPath` is consulted.
 *
 * @typedef {Object} RouteLike
 * @property {string} [fullPath] the full resolved path including query/hash.
 */

/**
 * Decide whether the Reload_Guard should perform a full-page navigation for
 * this router navigation instead of a normal SPA transition.
 *
 * Returns `true` only when ALL of the following hold:
 *   1. auth allowed the navigation as a plain `next()` (not a block, not an
 *      auth-issued redirect) — `authDecision.allowed === true` and
 *      `authDecision.isRedirect` is falsy (Requirements 8.2, 8.6);
 *   2. an update is pending — `isUpdatePending === true` (Requirement 8.1);
 *   3. the destination differs from the current route —
 *      `to.fullPath !== from.fullPath` (Requirements 8.1, 8.6).
 *
 * In every other case it returns `false`, meaning the navigation should
 * proceed as an ordinary SPA transition (Requirement 8.2) and no reload should
 * occur for same-route navigations or auth redirects (Requirement 8.6).
 *
 * This function performs no I/O, reads no globals, and mutates nothing. It is a
 * total function of its arguments.
 *
 * @param {Object} params
 * @param {AuthDecision} params.authDecision  outcome of the existing auth logic.
 * @param {boolean} params.isUpdatePending    current Update_Pending_State
 *                                             (typically `isUpdatePending()`).
 * @param {RouteLike} params.to                the destination route.
 * @param {RouteLike} params.from              the current route.
 * @returns {boolean} true if a full-page reload should be performed.
 */
export function shouldFullPageReload({ authDecision, isUpdatePending, to, from } = {}) {
  // Auth must have allowed a plain navigation to `to`. A block or an
  // auth-issued redirect is never converted into a full-page reload.
  const authAllowedPlainNavigation =
    !!authDecision && authDecision.allowed === true && !authDecision.isRedirect;

  if (!authAllowedPlainNavigation) {
    return false;
  }

  // An update must actually be pending.
  if (isUpdatePending !== true) {
    return false;
  }

  // The destination must differ from the current route; same-route
  // navigations proceed as SPA transitions (Requirement 8.6).
  const toPath = to ? to.fullPath : undefined;
  const fromPath = from ? from.fullPath : undefined;

  return toPath !== fromPath;
}

export default shouldFullPageReload;
